import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  Bus,
  Eye,
  MapPin,
  Pencil,
  Plus,
  UserRoundCheck,
  Users,
  Unlink,
} from 'lucide-react';
import Alert from '../components/common/Alert';
import Badge from '../components/common/Badge';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import ClassificationStatus from '../components/common/ClassificationStatus';
import LoadingSpinner from '../components/common/LoadingSpinner';
import InitialCredentialsModal from '../components/common/InitialCredentialsModal';
import Modal from '../components/common/Modal';
import Checkbox from '../components/forms/Checkbox';
import Input from '../components/forms/Input';
import Select from '../components/forms/Select';
import VoterRegistrationFlow from '../components/forms/VoterRegistrationFlow';
import Pagination from '../components/tables/Pagination';
import Table from '../components/tables/Table';
import { ACTIONS, RESOURCES, ROLES } from '../config/accessControl';
import { VOTER_CLASSIFICATIONS } from '../config/voterClassification';
import { useAuth } from '../context/AuthContext';
import usePermissions from '../hooks/usePermissions';
import { voterService } from '../services';

const initialFilters = {
  cedula: '',
  nombre: '',
  telefono: '',
  coordinadorId: '',
  punteroId: '',
  ciudad: '',
  local: '',
  estado: '',
  classification: '',
  necesitaMovil: '',
  origen: '',
};

const statusOptions = [
  { value: 'confirmado', label: 'Confirmado' },
  { value: 'pendiente', label: 'Pendiente' },
  { value: 'no_contactado', label: 'No contactado' },
  { value: 'rechazado', label: 'Rechazado' },
];

const classificationOptions = [
  { value: VOTER_CLASSIFICATIONS.BLACK, label: 'Verde · Genera gasto' },
  { value: VOTER_CLASSIFICATIONS.WHITE, label: 'Blanco · No genera gasto' },
];

const originOptions = [
  { value: 'PADRON', label: 'Padrón electoral' },
  { value: 'MANUAL', label: 'Registro manual' },
  { value: 'IMPORTACION', label: 'Importación' },
];

const formatDate = (value) =>
  value
    ? new Intl.DateTimeFormat('es-PY', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }).format(new Date(value))
    : '—';

function VotersPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { can, canEdit } = usePermissions();
  const [voters, setVoters] = useState([]);
  const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0 });
  const [filters, setFilters] = useState(initialFilters);
  const [page, setPage] = useState(1);
  const [options, setOptions] = useState({ coordinators: [], leaders: [] });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [registrationOpen, setRegistrationOpen] = useState(false);
  const [modalMode, setModalMode] = useState(null);
  const [editingVoter, setEditingVoter] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [credentials, setCredentials] = useState(null);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  const canCreate = can(RESOURCES.VOTERS, ACTIONS.CREATE);

  const loadVoters = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const response = await voterService.getForActor(user, {
        ...filters,
        page,
        pageSize: 10,
      });
      setVoters(response.data);
      setMeta(response.meta);
      if (response.meta.page !== page) setPage(response.meta.page);
    } catch (error) {
      setLoadError(error.message);
    } finally {
      setLoading(false);
    }
  }, [filters, page, user]);

  useEffect(() => {
    const timer = setTimeout(loadVoters, 180);
    return () => clearTimeout(timer);
  }, [loadVoters]);

  useEffect(() => {
    let mounted = true;
    voterService.getFilterOptions(user)
      .then((response) => mounted && setOptions(response.data))
      .catch(() => mounted && setOptions({ coordinators: [], leaders: [] }));
    return () => { mounted = false; };
  }, [user]);

  useEffect(() => {
    setPage(1);
  }, [filters]);

  const filteredLeaderOptions = useMemo(() =>
    options.leaders
      .filter((leader) => !filters.coordinadorId
        || String(leader.coordinatorId) === String(filters.coordinadorId))
      .map((leader) => ({ value: leader.id, label: leader.name })),
  [filters.coordinadorId, options.leaders]);

  const updateFilter = (field, value) =>
    setFilters((current) => ({
      ...current,
      [field]: value,
      ...(field === 'coordinadorId' ? { punteroId: '' } : {}),
    }));

  const openEdit = (voter) => {
    setEditingVoter(voter);
    setFormError('');
    reset(voter);
    setModalMode('edit');
  };

  const closeModal = () => {
    if (saving) return;
    setModalMode(null);
    setEditingVoter(null);
  };

  const submitVoter = async (values) => {
    setSaving(true);
    setFormError('');
    try {
      const payload = {
        ...values,
        necesita_movil: Boolean(values.necesita_movil),
        mesa: values.mesa ? Number(values.mesa) : '',
      };
      await voterService.updateForActor(editingVoter.id, payload, user);
      setFeedback('Votante actualizado correctamente.');
      setModalMode(null);
      setEditingVoter(null);
      await loadVoters();
    } catch (error) {
      setFormError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const removeFromLeader = async (voter) => {
    const confirmed = window.confirm(
      `¿Quitar a ${voter.nombreCompleto} del puntero ${voter.punteroNombre}? El registro quedará en auditoría.`
    );
    if (!confirmed) return;
    setLoadError('');
    try {
      await voterService.removeFromLeader(voter.id);
      setFeedback('El votante fue quitado operativamente del puntero.');
      await loadVoters();
    } catch (error) {
      setLoadError(error.message);
    }
  };

  const leaderFormOptions = options.leaders.map((leader) => ({
    value: leader.id,
    label: leader.name,
  }));
  const canChangeLeader =
    user.role !== ROLES.PUNTERO &&
    user.role !== ROLES.ADMINISTRADOR_GENERAL &&
    canEdit(RESOURCES.VOTERS, 'puntero_id');

  const allColumns = [
    {
      key: 'cedula',
      header: 'Cédula',
      render: (value) => <span className="font-semibold text-content-primary">{value}</span>,
    },
    {
      key: 'nombreCompleto',
      header: 'Nombre completo',
      render: (value) => <span className="min-w-44 font-medium text-content-primary">{value}</span>,
    },
    { key: 'telefono', header: 'Teléfono' },
    { key: 'coordinadorNombre', header: 'Coordinador' },
    { key: 'punteroNombre', header: 'Puntero' },
    { key: 'ciudad', header: 'Ciudad' },
    { key: 'local_votacion', header: 'Local de votación' },
    { key: 'mesa', header: 'Mesa', align: 'right' },
    {
      key: 'classification',
      header: 'Clasificación',
      render: (value, row) => (
        <div className="min-w-64">
          <ClassificationStatus
            classification={value}
            generatesExpense={row.classification === VOTER_CLASSIFICATIONS.BLACK}
            compact
          />
        </div>
      ),
    },
    {
      key: 'estado',
      header: 'Estado',
      render: (value) => (
        <Badge variant={value === 'confirmado' ? 'success' : value === 'pendiente' ? 'warning' : 'neutral'} dot>
          {statusOptions.find((item) => item.value === value)?.label || value}
        </Badge>
      ),
    },
    {
      key: 'necesita_movil',
      header: 'Necesita móvil',
      render: (value) => value ? <Badge variant="info">Sí</Badge> : <span className="text-content-muted">No</span>,
    },
    { key: 'origen', header: 'Origen' },
    {
      key: 'fecha_registro',
      header: 'Fecha de registro',
      render: formatDate,
    },
    {
      key: 'actions',
      header: 'Acciones',
      align: 'right',
      render: (_, row) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/votantes/${row.id}`)} aria-label={`Ver ${row.nombreCompleto}`}>
            <Eye className="size-4" />
          </Button>
          {user.role !== ROLES.PUNTERO && can(RESOURCES.VOTERS, ACTIONS.EDIT) && (
            <Button variant="ghost" size="sm" onClick={() => openEdit(row)} aria-label={`Editar ${row.nombreCompleto}`}>
              <Pencil className="size-4" />
            </Button>
          )}
          {user.role !== ROLES.PUNTERO && can(RESOURCES.VOTERS, ACTIONS.EDIT) && row.estado !== 'INACTIVE' && (
            <Button variant="ghost" size="sm" onClick={() => removeFromLeader(row)} aria-label={`Quitar ${row.nombreCompleto} del puntero`}>
              <Unlink className="size-4 text-danger-600" />
            </Button>
          )}
        </div>
      ),
    },
  ];
  const columns = user.role === ROLES.PUNTERO
    ? allColumns.filter((column) => [
        'cedula', 'nombreCompleto', 'telefono', 'local_votacion', 'mesa',
        'actions',
      ].includes(column.key))
    : allColumns;

  return (
    <div>
      <div className="flex flex-col justify-between gap-5 lg:flex-row lg:items-end">
        <div>
          <p className="text-sm font-semibold text-primary-700">Base territorial</p>
          <h2 className="mt-1 text-2xl font-semibold tracking-tight text-content-primary">Votantes</h2>
          <p className="mt-1 text-sm text-content-muted">Consulta los votantes disponibles dentro de tu estructura electoral.</p>
        </div>
        {canCreate && <Button onClick={() => setRegistrationOpen(true)}><Plus className="size-4" /> Registrar votante</Button>}
      </div>

      {feedback && <Alert variant="success" title="Operación completada" className="mt-5" onClose={() => setFeedback('')}>{feedback}</Alert>}
      {loadError && <Alert variant="danger" title="No se pudo cargar el listado" className="mt-5">{loadError}</Alert>}

      <div className={`mt-6 grid gap-4 ${user.role === ROLES.PUNTERO ? 'sm:grid-cols-1' : 'sm:grid-cols-3'}`}>
        <Card><div className="flex items-center justify-between"><div><p className="text-sm text-content-muted">Votantes encontrados</p><p className="mt-1 text-2xl font-semibold">{meta.total}</p></div><span className="grid size-10 place-items-center rounded-control bg-primary-50 text-primary-700"><Users className="size-5" /></span></div></Card>
        {user.role !== ROLES.PUNTERO && <Card><div className="flex items-center justify-between"><div><p className="text-sm text-content-muted">Confirmados en página</p><p className="mt-1 text-2xl font-semibold">{voters.filter((item) => item.estado === 'confirmado').length}</p></div><span className="grid size-10 place-items-center rounded-control bg-success-50 text-success-700"><UserRoundCheck className="size-5" /></span></div></Card>}
        {user.role !== ROLES.PUNTERO && <Card><div className="flex items-center justify-between"><div><p className="text-sm text-content-muted">Necesitan móvil</p><p className="mt-1 text-2xl font-semibold">{voters.filter((item) => item.necesita_movil).length}</p></div><span className="grid size-10 place-items-center rounded-control bg-info-50 text-info-700"><Bus className="size-5" /></span></div></Card>}
      </div>

      <Card className="mt-5" title="Filtros" description="Combina criterios para encontrar registros específicos.">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Input id="filter-document" label="Cédula" value={filters.cedula} onChange={(event) => updateFilter('cedula', event.target.value)} placeholder="Ej. 4582921" />
          <Input id="filter-name" label="Nombre" value={filters.nombre} onChange={(event) => updateFilter('nombre', event.target.value)} placeholder="Nombre o apellido" />
          <Input id="filter-phone" label="Teléfono" value={filters.telefono} onChange={(event) => updateFilter('telefono', event.target.value)} placeholder="Ej. 0981" />
          {user.role !== ROLES.PUNTERO && <Select id="filter-coordinator" label="Coordinador" value={filters.coordinadorId} onChange={(event) => updateFilter('coordinadorId', event.target.value)} placeholder="Todos" options={options.coordinators.map((item) => ({ value: item.id, label: item.name }))} />}
          {user.role !== ROLES.PUNTERO && <Select id="filter-leader" label="Puntero" value={filters.punteroId} onChange={(event) => updateFilter('punteroId', event.target.value)} placeholder="Todos" options={filteredLeaderOptions} />}
          {user.role !== ROLES.PUNTERO && <Input id="filter-city" label="Ciudad" value={filters.ciudad} onChange={(event) => updateFilter('ciudad', event.target.value)} />}
          <Input id="filter-place" label="Local" value={filters.local} onChange={(event) => updateFilter('local', event.target.value)} />
          {user.role !== ROLES.PUNTERO && <Select id="filter-status" label="Estado" value={filters.estado} onChange={(event) => updateFilter('estado', event.target.value)} placeholder="Todos" options={statusOptions} />}
          {user.role !== ROLES.PUNTERO && <Select id="filter-classification" label="Clasificación" value={filters.classification} onChange={(event) => updateFilter('classification', event.target.value)} placeholder="Todas" options={classificationOptions} />}
          {user.role !== ROLES.PUNTERO && <Select id="filter-mobile" label="Necesidad de móvil" value={filters.necesitaMovil} onChange={(event) => updateFilter('necesitaMovil', event.target.value)} placeholder="Todos" options={[{ value: 'true', label: 'Necesita móvil' }, { value: 'false', label: 'No necesita móvil' }]} />}
          {user.role !== ROLES.PUNTERO && <Select id="filter-origin" label="Origen del registro" value={filters.origen} onChange={(event) => updateFilter('origen', event.target.value)} placeholder="Todos" options={originOptions} />}
          <div className="flex items-end"><Button variant="ghost" className="w-full" onClick={() => setFilters(initialFilters)}>Limpiar filtros</Button></div>
        </div>
      </Card>

      <div className="mt-5">
        {loading ? (
          <Card><div className="grid min-h-52 place-items-center"><LoadingSpinner label="Cargando votantes" /></div></Card>
        ) : (
          <>
            <Table columns={columns} data={voters} emptyMessage="No hay votantes que coincidan con los filtros." />
            {meta.totalPages > 1 && <div className="mt-4"><Pagination page={meta.page} totalPages={meta.totalPages} onPageChange={setPage} /></div>}
          </>
        )}
      </div>

      <Modal
        open={modalMode === 'edit'}
        onClose={closeModal}
        size="lg"
        title="Editar votante"
        description="La relación con coordinador y administrador general se deriva automáticamente del puntero."
        footer={
          <>
            <Button variant="ghost" onClick={closeModal} disabled={saving}>Cancelar</Button>
            <Button type="submit" form="voter-form" loading={saving}>Guardar votante</Button>
          </>
        }
      >
        {formError && <Alert variant="danger" title="No se pudo guardar" className="mb-4">{formError}</Alert>}
        <form id="voter-form" onSubmit={handleSubmit(submitVoter)} className="grid gap-4 sm:grid-cols-2" noValidate>
          <Input id="voter-document" label="Cédula" required disabled={modalMode === 'edit' && !canEdit(RESOURCES.VOTERS, 'cedula')} error={errors.cedula?.message} {...register('cedula', { required: 'La cédula es obligatoria.' })} />
          <Input id="voter-phone" label="Teléfono" required disabled={modalMode === 'edit' && !canEdit(RESOURCES.VOTERS, 'telefono')} error={errors.telefono?.message} {...register('telefono', { required: 'El teléfono es obligatorio.' })} />
          <Input id="voter-first-name" label="Nombres" required disabled={modalMode === 'edit' && !canEdit(RESOURCES.VOTERS, 'nombres')} error={errors.nombres?.message} {...register('nombres', { required: 'Los nombres son obligatorios.' })} />
          <Input id="voter-last-name" label="Apellidos" required disabled={modalMode === 'edit' && !canEdit(RESOURCES.VOTERS, 'apellidos')} error={errors.apellidos?.message} {...register('apellidos', { required: 'Los apellidos son obligatorios.' })} />

          {canChangeLeader ? (
            <Select id="voter-leader" label="Puntero" placeholder="Seleccionar puntero" required options={leaderFormOptions} error={errors.puntero_id?.message} {...register('puntero_id', { required: 'Debes seleccionar un puntero.' })} />
          ) : (
            <Input id="voter-assigned-leader" label="Puntero" value={editingVoter?.punteroNombre || user.name} disabled readOnly hint="La relación se asigna automáticamente o no puede cambiarse con tu rol." />
          )}
          <Input id="voter-city" label="Ciudad" disabled={modalMode === 'edit' && !canEdit(RESOURCES.VOTERS, 'ciudad')} {...register('ciudad')} />
          <Input id="voter-place" label="Local de votación" disabled={modalMode === 'edit' && !canEdit(RESOURCES.VOTERS, 'local_votacion')} {...register('local_votacion')} />
          <Input id="voter-table" type="number" label="Mesa" disabled={modalMode === 'edit' && !canEdit(RESOURCES.VOTERS, 'mesa')} {...register('mesa')} />
          <Select id="voter-status" label="Estado" options={statusOptions} disabled={modalMode === 'edit' && !canEdit(RESOURCES.VOTERS, 'estado')} {...register('estado')} />
          <Select id="voter-origin" label="Origen" options={originOptions} disabled={modalMode === 'edit'} {...register('origen')} />
          <div className="flex items-end pb-1">
            <Checkbox id="voter-mobile" label="Necesita móvil" description="Incluir en la planificación de traslados." disabled={modalMode === 'edit' && !canEdit(RESOURCES.VOTERS, 'necesita_movil')} {...register('necesita_movil')} />
          </div>
          <div className="sm:col-span-2 rounded-control bg-slate-50 p-3 text-xs text-content-muted">
            <MapPin className="mr-1.5 inline size-4" />
            El sistema guardará únicamente `puntero_id`; los responsables superiores se resuelven desde el puntero.
          </div>
        </form>
      </Modal>

      <VoterRegistrationFlow
        open={registrationOpen}
        onClose={() => setRegistrationOpen(false)}
        user={user}
        onSuccess={async (created, meta = {}) => {
          setRegistrationOpen(false);
          setFeedback(meta.repeatedAssociation
            ? 'Votante registrado con el nuevo puntero. El dashboard mostrará la cédula repetida.'
            : 'Votante registrado correctamente.');
          if (created.initial_password) {
            setCredentials({
              username: created.username,
              initialPassword: created.initial_password,
            });
          }
          await loadVoters();
        }}
      />
      <InitialCredentialsModal
        credentials={credentials}
        onClose={() => setCredentials(null)}
      />
    </div>
  );
}

export default VotersPage;
